import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type KeyboardEvent, useId, useState } from "react";
import { q } from "./api.ts";
import { notify } from "./feedback.ts";
import { AGENT_ASSIST_LABELS as L, COMMON_LABELS, type AgentAssistHost, type AgentAssistTask } from "./labels.ts";
import { readPref, writePref } from "./prefs.ts";
import { Button } from "./ui.tsx";
import { AgentLogo } from "./ui/props.tsx";

// A hard setup handed to the person's agent in one sentence (docs/design-system.md
// §0.4l; docs/product-decisions.md 2026-11-01). The person says what they want,
// copies a prompt for Claude Code or Codex and applies what the agent proposes
// in the inbox. The browser only copies the person's words: the local launcher
// never takes a prompt from a page (plugin/spec/launcher.md). The prompt stays
// folded away until the person asks to see it.

const NAME: Record<AgentAssistHost, string> = { claude: "Claude Code", codex: "Codex" };
const PREF = "agent-assist-host";
const preferredHost = (): AgentAssistHost => (readPref(PREF) === "codex" ? "codex" : "claude");

export function AgentAssist({ productId, task, primary = false, folded = false }: {
  productId: string;
  task: AgentAssistTask;
  /** The main path on its screen: the last agent the person used is the primary button. */
  primary?: boolean;
  /** Folded under its title, beside a form the person may rather fill in. */
  folded?: boolean;
}) {
  const id = useId();
  const text = L.task[task];
  const product = useQuery(q.product(productId));
  const [request, setRequest] = useState("");
  const [host, setHost] = useState<AgentAssistHost>(preferredHost);
  const [copied, setCopied] = useState<AgentAssistHost | null>(null);
  const target = { id: productId, name: product.data?.name ?? productId };
  const copy = async (next: AgentAssistHost) => {
    setHost(next);
    try {
      await navigator.clipboard.writeText(L.prompt(task, next, target, request));
      writePref(PREF, next);
      setCopied(next);
      notify.success(COMMON_LABELS.copied);
    } catch { notify.error(COMMON_LABELS.copyFailed); }
  };
  // Not a form: it often sits inside one, and Enter must not submit that form.
  const enter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void copy(host);
  };
  const hosts: AgentAssistHost[] = host === "codex" ? ["codex", "claude"] : ["claude", "codex"];
  const body = <div className={`z-agent-assist${folded ? " is-folded" : ""}`} role="group" aria-labelledby={`${id}-title`}>
    {!folded && <label id={`${id}-title`} className="z-agent-assist-title" htmlFor={`${id}-input`}>{text.title}</label>}
    <input id={`${id}-input`} className="z-input" value={request} maxLength={300} placeholder={text.placeholder} autoComplete="off"
      aria-label={folded ? text.title : undefined} aria-describedby={`${id}-note`}
      onChange={(event) => { setRequest(event.target.value); setCopied(null); }} onKeyDown={enter} />
    <div className="z-inline">
      {hosts.map((option, index) => <Button key={option} type="button" size={folded ? "sm" : undefined} tone={primary && index === 0 ? "primary" : undefined} onClick={() => void copy(option)}>
        <AgentLogo engine={option === "codex" ? "codex" : "claude_code"} />{option === "codex" ? L.copyCodex : L.copyClaude}
      </Button>)}
    </div>
    <p id={`${id}-note`} className="z-small z-muted" role="status">
      {copied ? <>{L.copied(NAME[copied])} <Link className="z-link" to="/inbox">{L.openInbox}</Link></> : text.note}
    </p>
    <details className="z-disclosure">
      <summary>{L.preview}</summary>
      <div className="z-cli"><pre>{L.prompt(task, host, target, request)}</pre></div>
    </details>
  </div>;
  if (!folded) return body;
  return <details className="z-disclosure z-agent-assist-fold">
    <summary id={`${id}-title`}>{text.title}</summary>
    {body}
  </details>;
}
